import type { PhoneSettingsUpdateInput } from '@cp3/shared';
import { TWILIO_CONVERSATION_RELAY_URL, TELNYX_MEDIA_FORK_URL } from '../../../lib/voiceAgent';

export type FormState = PhoneSettingsUpdateInput;

export const DEFAULT_FORM_STATE: FormState = {
  telephonyProvider: 'twilio',
  twilio: {
    accountSid: '',
    phoneNumber: '',
    conversationRelayUrl: TWILIO_CONVERSATION_RELAY_URL,
  },
  llmProvider: 'openai',
  llmModel: 'gpt-4o-mini',
  llmTemperature: 0.3,
  llmMaxTokens: 200,
  systemPrompt:
    'Du bist ein freundlicher, kompetenter Telefon-Assistent. Halte dich kurz, ' +
    'frage nach Name und Anliegen, schlage passende Termine aus den verfügbaren ' +
    'Zeitfenstern vor und bestätige Buchungen klar.',
  knowledgeBase: '',
  introPhrase: 'Hallo, mein Name ist Lisa. Wie kann ich Ihnen heute helfen?',
  introAudioUrl: null,
  outroPhrase: 'Vielen Dank für Ihren Anruf. Auf Wiederhören!',
  selectedAgentPresetId: null,
  // UI-Toggle für die Beratungs-Variante des Conversation-Style-Prompts.
  // Default `false` = sachlich-effiziente Variante.
  consultativeMode: false,
  sttProvider: 'deepgram',
  ttsProvider: 'elevenlabs',
  ttsVoiceId: '21m00Tcm4TlvDq8ikWAM',
  ttsModel: 'eleven_flash_v2_5',
  ttsStability: 0.5,
  ttsSimilarityBoost: 0.75,
  fillerEnabled: true,
  fillerThresholdMs: 800,
  fillerStrategy: 'context-aware',
  fillerCategories: [
    { category: 'mhm', enabled: true, minDelayMs: 600, maxPerCall: 3 },
    { category: 'wait-phrase', enabled: true, minDelayMs: 1500, maxPerCall: 2 },
    { category: 'breath', enabled: true, minDelayMs: 800, maxPerCall: 4 },
    { category: 'filler-word', enabled: true, minDelayMs: 700, maxPerCall: 3 },
    { category: 'throat-clear', enabled: false, minDelayMs: 0, maxPerCall: 1 },
    { category: 'cough', enabled: false, minDelayMs: 0, maxPerCall: 1 },
  ],
  maxFillerPerCall: 5,
  fillerAlwaysPlay: false,
  // Admin-only routing flag — defaults to using personal keys.
  useCentralApiKeys: false,
  voiceAgentMode: 'classic',
  nativeVoiceAgent: null,
  language: 'de-DE',
  // Behaviour-rule overrides default to null → voice-agent uses the
  // matching DEFAULT_PROMPT_* constant from `promptDefaults.ts`. The
  // UI shows the default as the textarea's placeholder so users can
  // see what they'd be overriding.
  promptCallerIdSecurity: null,
  promptConversationStyle: null,
  promptAppointmentWorkflow: null,
  promptTicketCreation: null,
  promptTicketLookup: null,
  promptTransferRules: null,
  // Transfer feature defaults — feature is OFF until the user configures
  // a directory + picks a mode in `/settings/transfer`.
  transferMode: 'off',
  transferTargets: [],
  trustedCallers: [],
  transferFallbackTargetId: null,
  // Empty array → voice-agent treats this as a single implicit
  // "Standard" slot, preserving single-resource behaviour for tenants
  // that haven't configured slots yet.
  bookingSlots: [],
  // LiveKit add-on — defaults preserve the existing Twilio Media-
  // Streams path. The tenant flips `pipelineTransport` to `'livekit'`
  // only after they've saved valid LK credentials.
  pipelineTransport: 'twilio-media',
  livekitUrl: null,
  livekitSipUri: null,
};

/** Strict equality test for unsaved-state detection. */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => deepEqual(v, b[i]));
  }
  const ak = Object.keys(a as Record<string, unknown>);
  const bk = Object.keys(b as Record<string, unknown>);
  if (ak.length !== bk.length) return false;
  return ak.every((k) =>
    deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
  );
}

/** Drop the secrets block from a form state for dirty-checking against the
 *  server-loaded settings (the server never returns secrets). */
export function withoutSecrets(s: FormState): Omit<FormState, 'secrets'> {
  const { secrets: _secrets, ...rest } = s;
  return rest;
}

export type Tab =
  | 'provider'
  | 'agent'
  | 'llm'
  | 'voice'
  | 'calendar'
  | 'filler'
  | 'apikeys'
  | 'transfer';

export const TAB_ORDER: Tab[] = [
  'provider',
  'agent',
  'llm',
  'voice',
  'calendar',
  'filler',
  'apikeys',
  'transfer',
];
